// /opt/ptg01-v1/front-extranet/lib/ToastContext.tsx
// Système de toasts avec animation fade-in / fade-out progressive + barre de progression.
// Pattern : Context + hook useToast.
//
// Cycle de vie d'un toast :
//   1. Mount → entry animation (slide+fade) 250ms
//   2. Vie (3000ms) → progress bar shrink 100% → 0%
//   3. Avant suppression : exit animation (fade-out + slide-down) 300ms
//   4. Removal effectif après animation
//
// Charte Nexvia : bordure colorée gauche + barre progression colorée bas.
'use client';

import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { CheckCircle2, AlertCircle, Info, X } from 'lucide-react';

type ToastVariant = 'success' | 'error' | 'info';

interface ToastAction {
    label: string;
    onClick: () => void;
}

interface ToastOptions {
    /** Action contextuelle (ex: "Annuler" sur action réversible — pattern Undo) */
    action?: ToastAction;
}

interface Toast {
    id: number;
    variant: ToastVariant;
    message: string;
    dismissing: boolean;     // true = en cours de fade-out
    action?: ToastAction;
}

interface ToastApi {
    success: (msg: string, opts?: ToastOptions) => void;
    error: (msg: string, opts?: ToastOptions) => void;
    info: (msg: string, opts?: ToastOptions) => void;
}

const ToastCtx = createContext<ToastApi>({ success: () => {}, error: () => {}, info: () => {} });

export function useToast() {
    return useContext(ToastCtx);
}

const VARIANT_STYLES: Record<ToastVariant, { border: string; bar: string; iconClass: string; Icon: typeof CheckCircle2 }> = {
    success: { border: 'border-l-emerald-500', bar: 'bg-emerald-500', iconClass: 'text-emerald-600', Icon: CheckCircle2 },
    error:   { border: 'border-l-rose-500',    bar: 'bg-rose-500',    iconClass: 'text-rose-600',    Icon: AlertCircle },
    info:    { border: 'border-l-[#000091]',   bar: 'bg-[#000091]',   iconClass: 'text-[#000091]',   Icon: Info },
};

const VISIBLE_MS = 3000;        // durée pendant laquelle le toast est pleinement visible
const FADE_OUT_MS = 300;        // durée du fade-out

export function ToastProvider({ children }: { children: React.ReactNode }) {
    const [toasts, setToasts] = useState<Toast[]>([]);

    // Marque un toast comme "en cours de fade-out". Suppression effective après FADE_OUT_MS.
    const startDismiss = useCallback((id: number) => {
        setToasts(prev => prev.map(t => t.id === id ? { ...t, dismissing: true } : t));
        setTimeout(() => {
            setToasts(prev => prev.filter(t => t.id !== id));
        }, FADE_OUT_MS);
    }, []);

    const push = useCallback((variant: ToastVariant, message: string, opts?: ToastOptions) => {
        const id = Date.now() + Math.random();
        setToasts(prev => [...prev, { id, variant, message, dismissing: false, action: opts?.action }]);
        setTimeout(() => startDismiss(id), VISIBLE_MS);
    }, [startDismiss]);

    const api = useMemo<ToastApi>(() => ({
        success: (msg, opts) => push('success', msg, opts),
        error:   (msg, opts) => push('error',   msg, opts),
        info:    (msg, opts) => push('info',    msg, opts),
    }), [push]);

    return (
        <ToastCtx.Provider value={api}>
            {children}
            {/* Container : bottom-right desktop, bottom-center mobile */}
            <div
                className="pointer-events-none fixed left-1/2 -translate-x-1/2 sm:left-auto sm:right-4 sm:translate-x-0 z-[60] flex flex-col gap-2 px-4 sm:px-0 max-w-[400px] w-[calc(100vw-2rem)] sm:w-auto"
                style={{ bottom: 'calc(80px + env(safe-area-inset-bottom))' }}
                aria-live="polite"
                role="status"
            >
                {toasts.map(t => {
                    const v = VARIANT_STYLES[t.variant];
                    return (
                        <div
                            key={t.id}
                            className={`pointer-events-auto relative bg-white border-2 ${v.border} border-slate-200 rounded-xl shadow-lg shadow-slate-900/10 px-3 py-2.5 flex items-start gap-2.5 overflow-hidden transition-all duration-300 ease-out ${
                                t.dismissing
                                    ? 'opacity-0 translate-y-2 scale-95'
                                    : 'opacity-100 translate-y-0 scale-100 animate-toast-in'
                            }`}
                        >
                            <v.Icon className={`w-4 h-4 shrink-0 mt-0.5 ${v.iconClass}`} />
                            <span className="text-[13px] text-slate-800 flex-1 leading-snug">{t.message}</span>
                            {t.action && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        t.action!.onClick();
                                        startDismiss(t.id);
                                    }}
                                    className={`text-[12px] font-bold ${v.iconClass} hover:underline shrink-0 mr-1`}
                                >
                                    {t.action.label}
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={() => startDismiss(t.id)}
                                className="text-slate-400 hover:text-slate-600 shrink-0"
                                aria-label="Fermer"
                            >
                                <X className="w-3.5 h-3.5" />
                            </button>
                            {/* Barre de progression (compte à rebours visuel) */}
                            {!t.dismissing && (
                                <span
                                    className={`absolute bottom-0 left-0 h-[3px] ${v.bar} animate-toast-progress`}
                                />
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Animations CSS (scoped en style global via dangerouslySetInnerHTML pour éviter
                d'ajouter une dépendance Tailwind plugin). */}
            <style jsx global>{`
                @keyframes toast-in {
                    from { opacity: 0; transform: translateY(8px) scale(0.96); }
                    to   { opacity: 1; transform: translateY(0) scale(1); }
                }
                @keyframes toast-progress {
                    from { width: 100%; }
                    to   { width: 0%; }
                }
                .animate-toast-in {
                    animation: toast-in 250ms ease-out;
                }
                .animate-toast-progress {
                    animation: toast-progress ${VISIBLE_MS}ms linear forwards;
                }
            `}</style>
        </ToastCtx.Provider>
    );
}
